import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Barcode, Loader2, ChevronLeft, ChevronRight, ShoppingBag, Leaf, AlertCircle, ImageOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import Navigation from '@/components/layout/Navigation';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Safe image component - prevents XSS via innerHTML
const ProductImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);
  
  if (!src || hasError) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <ShoppingBag className="w-8 h-8 text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <img 
      src={src} 
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => setHasError(true)}
    />
  );
};

// Nutriscore colors
const nutriscoreColors = {
  a: 'bg-green-500 text-white',
  b: 'bg-lime-500 text-white',
  c: 'bg-yellow-500 text-black',
  d: 'bg-orange-500 text-white',
  e: 'bg-red-500 text-white',
};

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const SearchPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchSource, setSearchSource] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 0,
    total: 0
  });
  
  // Debounce search query (400ms delay)
  const debouncedQuery = useDebounce(query, 400);
  const lastSearchedQuery = useRef('');

  const searchProducts = useCallback(async (searchQuery, page = 1) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      if (page === 1) {
        setResults([]);
        setSearched(false);
      }
      return;
    }

    // Prevent duplicate searches
    if (page === 1 && searchQuery === lastSearchedQuery.current) {
      return;
    }
    
    lastSearchedQuery.current = searchQuery;
    setLoading(true);
    setSearched(true);

    try {
      const response = await axios.get(`${API}/api/search`, {
        params: {
          q: searchQuery.trim(),
          page,
          page_size: 20,
          country: 'uk'
        }
      });

      setResults(response.data.results || []);
      setSearchSource(response.data.source || 'Open Food Facts');
      setPagination({
        page: response.data.page || 1,
        totalPages: response.data.total_pages || 0,
        total: response.data.total || 0
      });
    } catch (error) {
      console.error('Search failed:', error);
      const errorMessage = error.response?.data?.detail || 'Search failed. Please try again.';
      toast.error(errorMessage);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-search when debounced query changes
  useEffect(() => {
    if (debouncedQuery && debouncedQuery.trim().length >= 2) {
      searchProducts(debouncedQuery, 1);
    } else if (debouncedQuery.trim().length === 0) {
      setResults([]);
      setSearched(false);
      lastSearchedQuery.current = '';
    }
  }, [debouncedQuery, searchProducts]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim().length >= 2) {
      lastSearchedQuery.current = ''; // Reset to force search
      searchProducts(query, 1);
    } else {
      toast.error('Please enter at least 2 characters');
    }
  };

  const handlePageChange = (newPage) => {
    searchProducts(query, newPage);
    window.scrollTo(0, 0);
  };

  const handleProductClick = (product) => {
    // Only navigate if we have a valid barcode
    if (product.barcode) {
      navigate(`/scan?barcode=${product.barcode}`);
    } else {
      // For FatSecret results without barcode, show toast
      toast.info('This product has no barcode. Try scanning the actual product.');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="search-page">
      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5DBDB3] to-[#4DA9A0] flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Search Products</h1>
          <p className="text-muted-foreground">
            Search millions of products from Open Food Facts & FatSecret
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <Input
                type="text"
                placeholder="Start typing to search... (e.g., Nutella, Heinz Beans)"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10 pr-10 h-12 text-lg"
                data-testid="search-input"
              />
              {loading && (
                <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 animate-spin text-[#5DBDB3]" />
              )}
            </div>
            <Button 
              type="submit" 
              className="h-12 px-6 bg-[#5DBDB3] hover:bg-[#4DA9A0]"
              disabled={loading || query.trim().length < 2}
              data-testid="search-button"
            >
              <Search className="w-5 h-5 sm:mr-2" />
              <span className="hidden sm:inline">Search</span>
            </Button>
          </div>
          {query.length > 0 && query.length < 2 && (
            <p className="text-xs text-muted-foreground mt-2 ml-1">
              Type at least 2 characters to search...
            </p>
          )}
        </form>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#5DBDB3]" />
            <span className="ml-3 text-muted-foreground">Searching products...</span>
          </div>
        ) : searched && results.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No products found</h3>
            <p className="text-muted-foreground">
              Try a different search term or check the spelling
            </p>
          </div>
        ) : results.length > 0 ? (
          <>
            {/* Results count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                Found <span className="font-semibold text-foreground">{pagination.total.toLocaleString()}</span> products
              </p>
              {pagination.totalPages > 1 && (
                <p className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.totalPages}
                </p>
              )}
            </div>

            {/* Product Grid */}
            <div className="grid gap-4 mb-8" data-testid="search-results">
              {results.map((product, index) => (
                <Card 
                  key={`${product.source || 'off'}-${product.barcode || product.food_id || index}`}
                  className={`hover:shadow-md transition-shadow border-border ${product.barcode ? 'cursor-pointer' : 'cursor-default opacity-80'}`}
                  onClick={() => handleProductClick(product)}
                  data-testid={`product-card-${product.barcode || product.food_id || index}`}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {/* Product Image */}
                      <div className="w-20 h-20 flex-shrink-0 rounded-lg bg-muted overflow-hidden">
                        <ProductImage 
                          src={product.image_url} 
                          alt={product.product_name} 
                        />
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg truncate mb-1">
                          {product.product_name}
                        </h3>
                        
                        {product.brand && (
                          <p className="text-sm text-muted-foreground mb-2">
                            {product.brand}
                          </p>
                        )}

                        <div className="flex flex-wrap items-center gap-2">
                          {/* Barcode - only show if available */}
                          {product.barcode ? (
                            <Badge variant="outline" className="text-xs">
                              <Barcode className="w-3 h-3 mr-1" />
                              {product.barcode}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs bg-gray-50 text-gray-500">
                              No barcode
                            </Badge>
                          )}

                          {/* Nutriscore */}
                          {product.nutriscore && (
                            <Badge className={`text-xs font-bold ${nutriscoreColors[product.nutriscore.toLowerCase()] || 'bg-gray-400'}`}>
                              Nutri-Score {product.nutriscore.toUpperCase()}
                            </Badge>
                          )}

                          {/* Has Ingredients */}
                          {product.has_ingredients && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                              <Leaf className="w-3 h-3 mr-1" />
                              Ingredients Available
                            </Badge>
                          )}
                          
                          {/* Source badge for FatSecret */}
                          {product.source === 'FatSecret' && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                              FatSecret
                            </Badge>
                          )}
                        </div>

                        {/* Categories */}
                        {product.categories && product.categories.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {product.categories.map((cat, idx) => (
                              <span key={idx} className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                                {cat.replace(/-/g, ' ')}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Arrow */}
                      <div className="flex items-center">
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  data-testid="prev-page"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {/* Show page numbers */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={pagination.page === pageNum ? "default" : "outline"}
                        size="sm"
                        className={pagination.page === pageNum ? "bg-[#5DBDB3]" : ""}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  data-testid="next-page"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        ) : (
          /* Initial state - show suggestions */
          <div className="text-center py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
              {['Nutella', 'Heinz Beans', 'Cadbury', 'Tesco', 'Walkers Crisps', 'Kellogg\'s', 'Coca Cola', 'Marmite'].map((suggestion) => (
                <Button
                  key={suggestion}
                  variant="outline"
                  className="h-auto py-3"
                  onClick={() => {
                    setQuery(suggestion);
                    searchProducts(suggestion, 1);
                  }}
                >
                  {suggestion}
                </Button>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Click a suggestion or type your own search
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default SearchPage;
